import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import {
	AlertCircle,
	FileIcon,
	Loader2,
	PenLineIcon,
	SaveIcon,
	Trash2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import {
	Filter,
	FilterContent,
	FilterItem,
	FilterTrigger,
} from "@/components/admin/Filter";
import TableHeadSortButton from "@/components/admin/TableHeadSortButton";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Container from "@/components/ui/container";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { UploadDropzone } from "@/components/ui/UploadDropzone";
import pages from "@/config/pages";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { Account } from "@/types/account/account";
import { AccountStatus } from "@/types/account/account";
import type { MainAccount } from "@/types/mainAccount/mainAccount";

const appService = new AppService();

type SortState = {
	field: "name" | "groups" | "email" | "phone_nr" | "status";
	direction: "asc" | "desc" | "none";
};

const SingleMainAccountPage = () => {
	const { mainAccountId } = useParams<{ mainAccountId: string }>();
	const [selectedNames, setSelectedNames] = useState(new Set<string>());
	const [selectedStatus, setSelectedStatus] = useState(new Set<string>());

	const [sortState, setSortState] = useState<SortState>({
		field: "name",
		direction: "asc",
	});

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Use cacheKeys for query keys
	const mainAccountCacheKey = ["mainAccount", mainAccountId];
	const accountsCacheKey = ["accounts", mainAccountId];

	// API Data Fetching
	const mainAccountQuery = useQuery<APIResponse<MainAccount>, Error>({
		queryKey: mainAccountCacheKey,
		queryFn: () => {
			return appService.getSingleMainAccount(mainAccountId!);
		},
		enabled: !!mainAccountId,
	});

	const accountsQuery = useQuery<APIResponse<Account[]>, Error>({
		queryKey: accountsCacheKey,
		queryFn: () => {
			return appService.getAccounts(mainAccountId!);
		},
	});

	const accountsData = useMemo<Account[]>(() => {
		return accountsQuery.data?.Result ?? [];
	}, [accountsQuery.data]);

	const optionsName = useMemo(
		() =>
			accountsData
				.map((acc) => ({
					value: acc.display_name,
					label: acc.display_name,
				}))
				.filter(
					(
						value,
						index,
						self, // Filter out duplicates based on label
					) => index === self.findIndex((t) => t.label === value.label),
				),
		[accountsData],
	);

	// Get all unique names from accountsData
	const optionsStatus = Object.values(AccountStatus).map((status) => ({
		label: status.charAt(0).toUpperCase() + status.slice(1),
		value: status,
	}));

	// State for document upload sheet
	const [isChooseFileDialogOpen, setIsChooseFileDialogOpen] = useState(false);
	const [chosenFiles, setChosenFiles] = useState<File[]>([]);

	//Delete Main Account
	async function deleteMainAccount() {
		if (!mainAccountId) {
			console.error("Main account ID is required");
			toast.error("Main account ID is required");
			return;
		}

		try {
			const promise = appService.deleteMainAccount(mainAccountId);
			toast.promise(promise, {
				loading: "Sletter kunde...",
				success: "Kunde slettet",
				error: "Noe gikk galt",
			});
			await promise;
			navigate(pages.Admin);
		} catch (error) {
			console.error("Error uploading data:", error);
		}
	}

	// Filter Toggle Functions
	const toggleNames = (value: string) => {
		const newValues = new Set(selectedNames);
		if (newValues.has(value)) {
			newValues.delete(value);
		} else {
			newValues.add(value);
		}
		setSelectedNames(newValues);
	};

	const toggleStatus = (value: string) => {
		const newValues = new Set(selectedStatus);
		if (newValues.has(value)) {
			newValues.delete(value);
		} else {
			newValues.add(value);
		}
		setSelectedStatus(newValues);
	};

	// Filtering and Sorting
	const filteredAndSortedAccounts = useMemo(() => {
		return accountsData
			.filter((acc) => {
				// name filter (if any names selected)
				const nameMatch =
					selectedNames.size === 0 || selectedNames.has(acc.display_name);
				// status filter (if any statuses selected)
				const statusMatch =
					selectedStatus.size === 0 || selectedStatus.has(acc.status);
				return nameMatch && statusMatch;
			})
			.sort((a, b) => {
				if (sortState.field === "name") {
					if (sortState.direction === "asc") {
						return a.display_name.localeCompare(b.display_name);
					} else if (sortState.direction === "desc") {
						return b.display_name.localeCompare(a.display_name);
					}
				} else if (sortState.field === "groups") {
					if (sortState.direction === "asc") {
						return (a.groups?.length || 0) - (b.groups?.length || 0);
					} else if (sortState.direction === "desc") {
						return (b.groups?.length || 0) - (a.groups?.length || 0);
					}
				} else if (sortState.field === "email") {
					if (sortState.direction === "asc") {
						return a.email.localeCompare(b.email);
					} else if (sortState.direction === "desc") {
						return b.email.localeCompare(a.email);
					}
				} else if (sortState.field === "phone_nr") {
					if (sortState.direction === "asc") {
						return a.phone_nr.localeCompare(b.phone_nr);
					} else if (sortState.direction === "desc") {
						return b.phone_nr.localeCompare(a.phone_nr);
					}
				} else if (sortState.field === "status") {
					if (sortState.direction === "asc") {
						return a.status.localeCompare(b.status);
					} else if (sortState.direction === "desc") {
						return b.status.localeCompare(a.status);
					}
				}
				// Default: no sort change
				return 0;
			});
	}, [sortState, accountsData, selectedNames, selectedStatus]);

	// Loading State
	if (mainAccountQuery.isPending || accountsQuery.isPaused) {
		return (
			<Container className="flex justify-center items-center py-20">
				<Loader2 className="h-8 w-8 animate-spin text-howden-blue" />
				<span className="ml-2">Laster inn kontoer...</span>
			</Container>
		);
	}

	// Error State
	if (mainAccountQuery.isError || accountsQuery.isError) {
		const error = mainAccountQuery.error || accountsQuery.error;
		const errorMessage = error?.message || "En ukjent feil oppstod";
		return (
			<Container className="py-10">
				<Card className="border-destructive bg-destructive/10">
					<CardHeader className="flex flex-row items-center space-x-2">
						<AlertCircle className="h-5 w-5 text-destructive" />
						<CardTitle className="text-destructive">
							Feil ved henting av kontoer
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p>Kunne ikke laste inn kontodata fra serveren.</p>
						<p className="text-sm text-muted-foreground mt-2">
							Detaljer: {errorMessage}
						</p>
						<Button
							variant="outline"
							size="sm"
							className="mt-4"
							onClick={() =>
								queryClient.invalidateQueries({
									queryKey: ["accounts", "all"],
								})
							}
						>
							Prøv igjen
						</Button>
					</CardContent>
				</Card>
			</Container>
		);
	}

	return (
		<div className="bg-soft-white dark:bg-[#121212] min-h-screen">
			<Container className="mx-auto">
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<Link
								to={pages.Admin}
								className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
							>
								{t("admin.home")}
							</Link>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<Link to={pages.AdminMainAccountDetails(mainAccountId)}>
								{mainAccountQuery.data?.Result.display_name}
							</Link>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
				{/* Company Details Section */}
				<Card className="mb-6 shadow-sm border">
					<CardContent className="p-6 space-y-6">
						<div className="space-y-2">
							<p className="text-sm font-medium text-howden-grey ">
								Main Account
							</p>
							<div className="flex flex-wrap gap-4 items-end">
								<h1 className="text-3xl font-bold font-header">
									{mainAccountQuery.data?.Result.display_name ||
										"Finner ikke navn"}
								</h1>
								{/*
								<p className="text-base font-medium text-howden-grey ">
									{mainAccountQuery.data?.Result.org_number || "Finner ikke orgnr"}
								</p>
								*/}
							</div>
						</div>
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
							<div className="flex flex-wrap gap-3">
								{/*
								{[
									{ icon: <MailIcon className="h-4 w-4" />, value: mainAccountQuery.data?.Result.email, label: "Email" },
									{ icon: <Smartphone className="h-4 w-4" />, value: mainAccountQuery.data?.Result.phone_nr, label: "Telefonnummer" },
									{ icon: <CircleUserIcon className="h-4 w-4" />, value: mainAccountQuery.data?.Result.contact_person, label: "Kontaktperson" },
								].map(({ icon, value, label }) => (
									<Button
										key={label}
										variant="secondary"
										size="sm"
										className="flex items-center gap-2 px-3 py-1.5 text-sm font-sans"
										onClick={() => {
											navigator.clipboard.writeText(value || "")
												.then(() => toast.success(`${label} kopiert!`))
												.catch(() => toast.error(`Kunne ikke kopiere ${label.toLowerCase()}`));
										}}
									>
										{icon}
										{value || `Ingen ${label.toLowerCase()}`}
										<CopyIcon className="h-4 w-4" />
									</Button>
								))}
								*/}
							</div>
							<div className="flex flex-wrap gap-3">
								<Button
									variant="outline"
									size="sm"
									className="px-3 py-1.5 text-sm font-sans border-moss-green-40 dark:border-pistachio-50 text-moss-green-95 dark:text-pistachio-95 hover:bg-pistachio-50 dark:hover:bg-pistachio-40"
									onClick={() =>
										navigate(
											pages.AdminMainAccountUpdate(
												mainAccountQuery.data?.Result.main_account_id,
											),
										)
									}
								>
									<PenLineIcon className="h-4 w-4 mr-1" />
									{t("admin.button.edit")}
								</Button>
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<Button
											variant="destructive"
											size="sm"
											className="px-3 py-1.5 text-sm font-sans"
										>
											<Trash2Icon className="h-4 w-4" />
											{t("admin.button.delete")} Main Account
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												ADVARSEL: Du er i ferd med å slette alle kunder. Dette
												vil slette alle tilknyttede kontoer, dokumenter og
												lenker. Denne handlingen kan ikke reverseres
											</AlertDialogTitle>
											<AlertDialogDescription>
												Er du sikker på at du vil slette Main Account:{" "}
												<b>{mainAccountQuery.data.Result.display_name}</b>?
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>Avbryt</AlertDialogCancel>
											<AlertDialogAction
												onClick={deleteMainAccount}
												className="bg-destructive hover:bg-destructive/80"
											>
												Slett
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Companies Overview Section */}
				<Dialog
					open={isChooseFileDialogOpen}
					onOpenChange={setIsChooseFileDialogOpen}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Last opp dokumenter</DialogTitle>
							<DialogDescription>Last opp dokumenter</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
							{chosenFiles.length === 0 && (
								<UploadDropzone
									onFilesAccepted={(acceptedFiles) => {
										setIsChooseFileDialogOpen(true);
										setChosenFiles(acceptedFiles);
									}}
								/>
							)}
							{chosenFiles.length > 0 &&
								chosenFiles.map((file) => {
									return (
										<div className="flex items-center gap-4">
											<div className="border flex-grow flex items-center gap-4 rounded px-4 py-2">
												<FileIcon />
												<div className="flex flex-grow flex-col gap-1">
													<input
														defaultValue={file.name}
														className="border-none text-sm bg-transparent focus-visible:ring-0 p-0"
													/>
													<Progress value={37} className="h-2 bg-slate-300" />
												</div>

												<Button
													variant="ghost"
													size="icon"
													className="shrink-0"
												>
													<Trash2Icon />
												</Button>
											</div>
										</div>
									);
								})}
						</div>
						<DialogFooter>
							<Button variant="outline">Avbryt</Button>
							<Button>
								<SaveIcon />
								Lagre
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<Card className="mb-6 shadow-sm border">
					<CardHeader className="p-6 ">
						<div className="flex gap-2 items-center justify-between">
							<div className="flex gap-2 items-center">
								<CardTitle className="text-2xl font-semibold font-header">
									Accounts
								</CardTitle>
								<Badge
									variant="secondary"
									className="text-sm rounded bg-howden-grey/20 h-4"
								>
									{filteredAndSortedAccounts.length} {t("admin.dashboard.row")}
								</Badge>
							</div>
							<div className="flex gap-2">
								<Filter>
									<FilterTrigger
										name="Accounts"
										selectedValues={selectedNames}
										options={optionsName}
									/>
									<FilterContent
										className="ml-8"
										selectedSize={selectedNames.size}
										clearSelected={() => setSelectedNames(new Set())}
									>
										{optionsName.map((option) => (
											<FilterItem
												key={option.value}
												option={option}
												isSelected={selectedNames.has(option.value)}
												toggleValue={toggleNames}
											/>
										))}
									</FilterContent>
								</Filter>
								<Filter>
									<FilterTrigger
										name="Status"
										selectedValues={selectedStatus}
										options={optionsStatus}
									/>
									<FilterContent
										className="ml-8"
										selectedSize={selectedStatus.size}
										clearSelected={() => setSelectedStatus(new Set())}
									>
										{optionsStatus.map((option) => (
											<FilterItem
												key={option.value}
												option={option}
												isSelected={selectedStatus.has(option.value)}
												toggleValue={toggleStatus}
											/>
										))}
									</FilterContent>
								</Filter>
							</div>
						</div>
					</CardHeader>

					{/* Companies Table */}
					<Table>
						<TableCaption>{t("admin.table.caption")}</TableCaption>
						<TableHeader>
							<TableRow>
								<TableHead>
									<TableHeadSortButton
										label="Account"
										sortDir={
											sortState.field == "name" ? sortState.direction : "none"
										}
										setSortDir={(dir) =>
											setSortState((prev) => ({
												...prev,
												field: "name",
												direction: dir,
											}))
										}
									/>
								</TableHead>
								<TableHead>
									<TableHeadSortButton
										label={t("admin.table.groups")}
										sortDir={
											sortState.field == "groups" ? sortState.direction : "none"
										}
										setSortDir={(dir) =>
											setSortState((prev) => ({
												...prev,
												field: "groups",
												direction: dir,
											}))
										}
									/>
								</TableHead>
								<TableHead>
									<TableHeadSortButton
										label={t("admin.table.email")}
										sortDir={
											sortState.field == "email" ? sortState.direction : "none"
										}
										setSortDir={(dir) =>
											setSortState((prev) => ({
												...prev,
												field: "email",
												direction: dir,
											}))
										}
									/>
								</TableHead>
								<TableHead>
									<TableHeadSortButton
										label={t("admin.table.phone")}
										sortDir={
											sortState.field == "phone_nr"
												? sortState.direction
												: "none"
										}
										setSortDir={(dir) =>
											setSortState((prev) => ({
												...prev,
												field: "phone_nr",
												direction: dir,
											}))
										}
									/>
								</TableHead>
								<TableHead>
									<TableHeadSortButton
										label="Status"
										sortDir={
											sortState.field == "status" ? sortState.direction : "none"
										}
										setSortDir={(dir) =>
											setSortState((prev) => ({
												...prev,
												field: "status",
												direction: dir,
											}))
										}
									/>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{/* checks if there is accounts */}
							{filteredAndSortedAccounts.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={6}
										className="h-24 text-center text-muted-foreground"
									>
										{" "}
										Ingen kontoer funnet{" "}
										{accountsData.length > 0 ? "med gjeldende filtre." : "."}
									</TableCell>
								</TableRow>
							) : (
								filteredAndSortedAccounts.map(
									(
										account, // Map over filtered/sorted data
									) => (
										<TableRow key={account.id} className=" ">
											{/* The Specific Account) */}
											<TableCell>
												<Button
													variant="link"
													className="dark:text-soft-white"
													onClick={() =>
														navigate(
															pages.AdminAccount(
																account.main_account_id, // Pass main account ID
																account.id, // Pass this specific account's ID
															),
														)
													}
												>
													{account.display_name}
												</Button>
											</TableCell>

											{/* Subgroups */}
											<TableCell className="gap-2 px-8">
												{/* SAFE ACCESS: nullish coalescing */}
												{(account.groups ?? []).length} {t("admin.table.group")}
												{(account.groups ?? []).length !== 1
													? t("admin.table.more")
													: ""}
											</TableCell>
											<TableCell className="gap-2 px-8">
												{account.email}
											</TableCell>
											<TableCell className="gap-2 px-8">
												{account.phone_nr}
											</TableCell>
											{/* Status Badge */}
											<TableCell>
												<Badge
													variant={
														account.status === AccountStatus.Active
															? "default"
															: account.status === AccountStatus.Suspended
																? "destructive"
																: account.status === AccountStatus.Inactive
																	? "secondary"
																	: "outline"
													}
												>
													{account.status}
												</Badge>
											</TableCell>
										</TableRow>
									),
								)
							)}
						</TableBody>
					</Table>
				</Card>
			</Container>
		</div>
	);
};

export default SingleMainAccountPage;
